import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { SaveProfileRequest, SaveProfileResponse } from "@myProfile/models";
import { openNotificationThunk } from "@store/Notification/thunks";

/**
 * Saves profile changes synchronously to Salesforce via contact-service-api.
 * API: POST contacts/v1/user/Profile (LPC-10934 — replaces the old SQS-backed
 * /entitlements/v1/user/UserProfile/updateProfile path).
 *
 * Response shape: { success, message, guid, emailChangeRequested, emailChangeCaseId }
 */
export const saveProfileThunk = createAsyncThunk<SaveProfileResponse, SaveProfileRequest, ThunkApiConfig>(
  "myProfile/saveProfile",
  async (profileData, { extra, dispatch, rejectWithValue }) => {
    try {
      const url = "contacts/v1/user/Profile";
      const response = await extra<SaveProfileResponse, SaveProfileRequest>(url, {
        method: "POST",
        body: profileData,
      });

      if (!response || !response.data) {
        return rejectWithValue("Failed to save profile");
      }

      const successText = response.data.emailChangeRequested
        ? "Profile updated. Email change request submitted for approval."
        : "Profile updated.";

      // Show success notification
      dispatch(
        openNotificationThunk({
          variant: "info",
          text: successText,
        })
      );

      return response.data;
    } catch (error: unknown) {
      let errorMessage = "Failed to save profile. Please try again.";

      if (typeof error === "object" && error !== null && "response" in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };

        errorMessage = axiosError.response?.data?.message || errorMessage;
      }

      dispatch(
        openNotificationThunk({
          variant: "warning",
          text: errorMessage,
        })
      );

      return rejectWithValue(errorMessage);
    }
  }
);
